import { useTranslation } from 'react-i18next';

import type { EtiquetaAyuda, PasoAyuda, PreguntaAyuda } from './pasos';

const RAIZ = '[data-testid="anual"]';
const MENSUALES = '[data-testid="pagos-fijos-mensuales"]';

function uno(selector: string): Element[] {
  const el = document.querySelector(selector);
  return el ? [el] : [];
}

function raiz(): HTMLElement | null {
  return document.querySelector<HTMLElement>(RAIZ);
}

/** Lo que hay en cada pestaña aunque no se vea (la raíz lo apunta). */
function cuantos(que: 'mensuales' | 'anuales' | 'cobro-largo'): number {
  const clave = {
    mensuales: 'nMensuales',
    anuales: 'nAnuales',
    'cobro-largo': 'nCobroLargo',
  }[que];
  return Number(raiz()?.dataset[clave] ?? 0);
}

function hayEstado(pestana: 'mensuales' | 'anuales', ...estados: string[]) {
  const lista = (
    raiz()?.dataset[
      pestana === 'mensuales' ? 'estadosMensuales' : 'estadosAnuales'
    ] ?? ''
  ).split(' ');
  return estados.some(e => lista.includes(e));
}

/**
 * La fila de ejemplo de «Mensuales»: una con día de cargo o, si no hay, la
 * primera. Siempre dentro de «Mensuales»: «Anuales» usa las mismas filas para
 * los cobros de 6-12 meses.
 */
function filaEjemplo(): Element | null {
  return (
    document.querySelector(
      `${MENSUALES} [data-testid="pago-fijo-fila"][data-ayuda-fila="normal"]`,
    ) ?? document.querySelector(`${MENSUALES} [data-testid="pago-fijo-fila"]`)
  );
}

function enFila(fila: Element | null, selector: string): Element[] {
  const el = fila?.querySelector(selector);
  return el ? [el] : [];
}

function estado(...estados: string[]): Element[] {
  for (const e of estados) {
    const el = document.querySelector(
      `${MENSUALES} [data-testid="pago-fijo-estado-${e}"]`,
    );
    if (el) {
      return [el];
    }
  }
  return [];
}

function vacio(pestana: 'mensuales' | 'anuales'): Element[] {
  return uno(`${RAIZ}[data-pestana="${pestana}"] [data-testid="estado-vacio"]`);
}

function filaAnual(...estados: string[]): Element[] {
  for (const e of estados) {
    const el = document.querySelector(
      `[data-testid="anual-fila"][data-estado="${e}"]`,
    );
    if (el) {
      return [el];
    }
  }
  return [];
}

function etiquetasFila(t: (texto: string) => string): EtiquetaAyuda[] {
  const fila = filaEjemplo();
  if (!fila) {
    return [];
  }
  const partes: [string, string, boolean][] = [
    ['[data-ayuda="dia"]', t('Charge day'), false],
    ['[data-ayuda="cuenta"]', t('Account'), true],
    ['[data-ayuda="importe"]', t('Amount'), false],
  ];
  return partes.flatMap(([selector, texto, debajo]) => {
    const el = fila.querySelector(selector);
    return el ? [{ el, texto, debajo }] : [];
  });
}

/**
 * La ayuda de «Pagos fijos» (maqueta aprobada por Vadik el 27-oct-2026):
 * recorre «Mensuales» y «Anuales» y ocho preguntas. Gavi solo cambia de
 * pestaña (y al cerrar deja la de la persona) y abre la brújula; nunca pulsa
 * «Es fijo», «No», el interruptor del Plan, una fila ni «Elegir grupos
 * anuales» (esa hoja guarda la selección al abrirse).
 */
export function usePreguntasPagosFijos(): PreguntaAyuda[] {
  const { t } = useTranslation();

  const conMensuales = () => cuantos('mensuales') > 0;
  // «Anuales» enseña sus gastos de una vez al año y, aparte, los pagos fijos
  // que se cobran cada 6-12 meses: la cabecera sale con cualquiera de ellos.
  const conGastosAnuales = () => cuantos('anuales') > 0;
  const conCobroLargo = () => cuantos('cobro-largo') > 0;
  const conAnuales = () => conGastosAnuales() || conCobroLargo();
  const mensual = (
    paso: Omit<PasoAyuda, 'escena'> & { escena?: PasoAyuda['escena'] },
  ): PasoAyuda => ({ escena: 'pagos-mensuales', ...paso });
  const anual = (
    paso: Omit<PasoAyuda, 'escena'> & { escena?: PasoAyuda['escena'] },
  ): PasoAyuda => ({ escena: 'pagos-anuales', ...paso });
  const fila = (): Element[] => {
    const f = filaEjemplo();
    return f ? [f] : [];
  };
  const encontrados = () => uno('[data-testid="gavi-encontrados"]');
  const hayEncontrados = () => Number(raiz()?.dataset.encontrados ?? 0) > 0;
  const hayExtras = () => raiz()?.dataset.extrasGavi === 'true';

  const todo: PasoAyuda[] = [
    {
      id: 'hola',
      escena: 'normal',
      radio: 14,
      texto: t(
        'Hi! A fixed payment is a bill that repeats: rent, electricity… I will tell you step by step. Mine are variable: I get paid when a tourist gets distracted with their chips.',
      ),
      objetivo: () => uno('[data-ayuda="titulo-pagos-fijos"]'),
    },
    {
      id: 'pestanas',
      escena: 'normal',
      radio: 14,
      texto: t(
        'Here you choose. "Monthly" is what is charged every month, every two or every three. "Annual", what is charged once a year or every six months.',
      ),
      objetivo: () => uno('[data-testid="pagos-fijos-pestana-segmentado"]'),
    },
    mensual({
      id: 'encontrados',
      radio: 16,
      disponible: hayEncontrados,
      texto: t(
        'I look at your transactions for charges that repeat and are not fixed payments yet. You decide: "It is fixed" adds it; with "No", I will not suggest it again.',
      ),
      objetivo: encontrados,
    }),
    mensual({
      id: 'vacio-mensuales',
      radio: 16,
      disponible: () => !conMensuales(),
      texto: t(
        'There are none yet. Open a category with a monthly target and turn on "Fixed payment". How to do it, in the questions.',
      ),
      objetivo: () => vacio('mensuales'),
    }),
    mensual({
      id: 'mes',
      radio: 16,
      disponible: conMensuales,
      texto: t(
        'At the top, what you have paid this month out of the total due. Below, the total from each account they come out of.',
      ),
      objetivo: () => [
        ...uno('[data-testid="pagos-fijos-cabecera"]'),
        ...uno('[data-testid="pagos-fijos-cuentas"]'),
      ],
    }),
    mensual({
      id: 'fila',
      radio: 12,
      disponible: conMensuales,
      texto: t(
        'Each row is a fixed payment: the day of the month it is charged, its name, the account it comes out of and how much is charged each time.',
      ),
      objetivo: fila,
      etiquetas: () => etiquetasFila(t),
    }),
    mensual({
      id: 'estado',
      radio: 14,
      disponible: conMensuales,
      texto: t(
        'On the right, how that payment is going: "Paid", "Due today", "On its way", "Late", "Coming up" or "Later". I explain each one in the questions.',
      ),
      objetivo: () =>
        enFila(filaEjemplo(), '[data-testid^="pago-fijo-estado-"]'),
    }),
    mensual({
      id: 'extras',
      radio: 12,
      disponible: hayExtras,
      texto: t(
        'This label is mine: that charge has gone up recently. For electricity and gas I compare with a year ago. If a category groups several payees, I split them.',
      ),
      objetivo: () => {
        const el = document.querySelector(
          `${MENSUALES} [data-testid="pago-fijo-sube"], ${MENSUALES} [data-testid="pago-fijo-interanual"], ${MENSUALES} [data-testid="pago-fijo-desglose"]`,
        );
        const f = el?.closest('[data-testid="pago-fijo-fila"]');
        return f ? [f] : [];
      },
    }),
    mensual({
      id: 'ficha',
      radio: 12,
      disponible: conMensuales,
      texto: t(
        'Tap a row and the card of that category opens: there you change the amount, the charge day and the account.',
      ),
      objetivo: fila,
    }),
    anual({
      id: 'anuales',
      radio: 18,
      disponible: conAnuales,
      texto: t(
        'In "Annual" are the once-a-year expenses (insurance, taxes…). At the top, what you have saved and the total of what is due in less than a year.',
      ),
      objetivo: () => uno('[data-testid="anual-cabecera"]'),
    }),
    anual({
      id: 'vacio-anuales',
      radio: 16,
      disponible: () => !conGastosAnuales(),
      texto: t(
        'What is paid once a year will show up here. Give a category a yearly target, or choose groups in the compass, with "Choose annual groups".',
      ),
      objetivo: () => vacio('anuales'),
    }),
    anual({
      id: 'apartar',
      radio: 14,
      disponible: conAnuales,
      texto: t(
        'This label says how much you need to set aside this month across all of them, to get there in time. Setting aside is assigning money to its category in the Plan.',
      ),
      objetivo: () => uno('[data-testid="anual-este-mes"]'),
    }),
    anual({
      id: 'plegar',
      radio: 16,
      disponible: conAnuales,
      texto: t(
        'With this switch, the Plan groups into a single line the annual expenses that are on track or already covered. The ones that are behind show up on their own.',
      ),
      objetivo: () => uno('[data-testid="anual-ocultar"]'),
    }),
    anual({
      id: 'cobro-largo',
      radio: 14,
      disponible: conCobroLargo,
      texto: t(
        'These are fixed payments you set aside for every month but that are charged every six or twelve months. They read the same as the "Monthly" ones.',
      ),
      objetivo: () => uno('[data-testid="pagos-fijos-cobro-largo"]'),
    }),
    anual({
      id: 'meses',
      radio: 12,
      disponible: conGastosAnuales,
      texto: t(
        'Below, by due month. Each row: amount, date, what is saved and what to set aside per month; the bar is how far you are. Tap it and its category opens.',
      ),
      objetivo: () => [
        ...uno('[data-testid="anual-mes"]'),
        ...uno('[data-testid="anual-tarjeta"]'),
      ],
      etiquetas: () => {
        const el = document.querySelector('[data-testid="anual-importe"]');
        return el ? [{ el, texto: t('Amount') }] : [];
      },
    }),
    {
      id: 'brujula',
      escena: 'brujula',
      radio: 20,
      texto: t(
        'To choose which groups count as annual spending, open the compass and tap "Choose annual groups". Only what you tick there shows up.',
      ),
      objetivo: () => uno('[data-testid="opcion-grupos-anuales"]'),
    },
    {
      id: 'final',
      escena: 'normal',
      radio: 24,
      final: true,
      texto: t(
        'Done! Tap the porthole whenever you want to go over it again. And if a bill turns up that you were not expecting, it was not me.',
      ),
      objetivo: () => uno('[data-testid="ojo-de-buey"]'),
    },
  ];

  return [
    { id: 'todo', texto: t('Tell me about this screen'), pasos: todo },
    {
      id: 'que-es',
      texto: t('What is a fixed payment and what is it for?'),
      pasos: [
        mensual({
          id: 'recibo',
          radio: 12,
          texto: t(
            'A fixed payment is a bill that repeats: rent, electricity, a subscription. Marking it lets me know when it is due and whether it has already gone out.',
          ),
          objetivo: () => {
            const f = fila();
            return f.length > 0 ? f : uno('[data-ayuda="titulo-pagos-fijos"]');
          },
        }),
        mensual({
          id: 'no-paga',
          radio: 14,
          disponible: conMensuales,
          texto: t(
            'Rumbo does not pay anything: it only plans for it and marks it "Paid" when it sees it has gone out. If it does not see it, you mark it in its card.',
          ),
          objetivo: () =>
            estado(
              'pagado',
              'atrasado',
              'en-camino',
              'proximo',
              'toca-hoy',
              'programado',
            ),
        }),
        {
          id: 'anual',
          escena: 'normal',
          radio: 14,
          final: true,
          texto: t(
            'What is paid once a year goes in "Annual", with what you have set aside to get there in time.',
          ),
          objetivo: () => uno('[data-testid="pagos-fijos-pestana-anuales"]'),
        },
      ],
    },
    {
      id: 'anadir',
      texto: t('How do I add or change a fixed payment?'),
      pasos: [
        mensual({
          id: 'gavi',
          radio: 16,
          disponible: hayEncontrados,
          texto: t(
            'The easiest way: if you see my card with a charge, tap "It is fixed". I add it and, if it has no target, I give it a monthly one with what is charged.',
          ),
          objetivo: encontrados,
        }),
        {
          id: 'tu',
          escena: 'normal',
          radio: 14,
          texto: t(
            'To do it yourself: in the Plan, open the category of the bill. Give it a monthly target (what you set aside each month) and turn on "Fixed payment" at the end of its card.',
          ),
          objetivo: () => uno('[data-ayuda="titulo-pagos-fijos"]'),
        },
        {
          id: 'objetivo',
          escena: 'normal',
          radio: 14,
          texto: t(
            'Without a target, the fixed payment does not show up in either list: the amount and how often it is charged come from the target.',
          ),
          objetivo: () => uno('[data-ayuda="titulo-pagos-fijos"]'),
        },
        mensual({
          id: 'cambiar',
          radio: 12,
          disponible: conMensuales,
          texto: t(
            'To change the charge day or the account, or to remove it, tap its row and go down to "Fixed payment". Removing it does not delete any transaction.',
          ),
          objetivo: fila,
        }),
        mensual({
          id: 'sin-dia',
          radio: 12,
          final: true,
          disponible: () => hayEstado('mensuales', 'sin-dia'),
          texto: t(
            'If it says "Set the charge day", I do not know yet which day it is charged. Open it and choose the day.',
          ),
          objetivo: () =>
            uno(
              `${MENSUALES} [data-testid="pago-fijo-fila"][data-ayuda-fila="sin-dia"]`,
            ),
        }),
      ],
    },
    {
      id: 'estados',
      texto: t('What do "Paid", "Late"… mean?'),
      pasos: [
        mensual({
          id: 'pagado',
          radio: 14,
          disponible: () => hayEstado('mensuales', 'pagado'),
          texto: t(
            '"Paid": it has already gone out this month. I notice when its category has spent almost all that was planned, or when you mark it in its card.',
          ),
          objetivo: () => estado('pagado'),
        }),
        mensual({
          id: 'hoy',
          radio: 14,
          disponible: () => hayEstado('mensuales', 'toca-hoy', 'en-camino'),
          texto: t(
            '"Due today": it is charged today. "On its way": up to three days have passed since the charge and I do not see it yet; it may take a while to arrive.',
          ),
          objetivo: () => estado('toca-hoy', 'en-camino'),
        }),
        mensual({
          id: 'atrasado',
          radio: 14,
          disponible: () => hayEstado('mensuales', 'atrasado'),
          texto: t(
            '"Late": more than three days have passed and I have not seen it go out. If you think you already paid it, look at the question "Late, but I already paid it".',
          ),
          objetivo: () => estado('atrasado'),
        }),
        mensual({
          id: 'proximo',
          radio: 14,
          final: true,
          disponible: () => hayEstado('mensuales', 'proximo', 'programado'),
          texto: t(
            '"Coming up": it is due in seven days or less. "Later": it is due further ahead, or I do not know yet which day it falls on.',
          ),
          objetivo: () => estado('proximo', 'programado'),
        }),
      ],
    },
    {
      id: 'ya-pagado',
      texto: t('It says "Late", but I already paid it'),
      pasos: [
        mensual({
          id: 'como-se',
          radio: 14,
          disponible: () => hayEstado('mensuales', 'atrasado', 'en-camino'),
          texto: t(
            'I find out a payment has gone out when it is recorded in its category. If you did not record it, or recorded it in another one, I still see it as unpaid.',
          ),
          objetivo: () => estado('atrasado', 'en-camino'),
        }),
        mensual({
          id: 'arreglo',
          radio: 12,
          final: true,
          disponible: () => hayEstado('mensuales', 'atrasado', 'en-camino'),
          texto: t(
            'Record it in Transactions with its category, or tap the row and, in its card, tap "Mark as paid this month".',
          ),
          objetivo: () => {
            const el = estado('atrasado', 'en-camino')[0];
            const f = el?.closest('[data-testid="pago-fijo-fila"]');
            return f ? [f] : [];
          },
        }),
      ],
    },
    {
      id: 'tarjeta-gavi',
      texto: t('What is that card with what you have found?'),
      pasos: [
        mensual({
          id: 'busco',
          radio: 16,
          disponible: hayEncontrados,
          texto: t(
            'I look at your transactions from the last two years for charges that repeat and are not here: at least three times and with similar amounts.',
          ),
          objetivo: encontrados,
        }),
        mensual({
          id: 'responder',
          radio: 12,
          final: true,
          disponible: hayEncontrados,
          texto: t(
            'With "It is fixed", that category becomes a fixed payment and shows up in the list below. With "No", I will not suggest that charge again.',
          ),
          objetivo: () => uno('[data-testid="gavi-encontrado"]'),
        }),
      ],
    },
    {
      id: 'sube',
      texto: t('What does "Up" mean?'),
      pasos: [
        mensual({
          id: 'subida',
          radio: 12,
          disponible: hayExtras,
          texto: t(
            'It is a recent increase: what that charge costs now compared with the previous one. I show it for about three months; if it is charged once a year, for a year.',
          ),
          objetivo: () => uno(`${MENSUALES} [data-testid="pago-fijo-sube"]`),
        }),
        mensual({
          id: 'interanual',
          radio: 12,
          disponible: hayExtras,
          texto: t(
            'For electricity and gas the bill changes with what you use. I compare the last six months with the same ones a year ago and warn you if it goes up 10% or more.',
          ),
          objetivo: () =>
            uno(`${MENSUALES} [data-testid="pago-fijo-interanual"]`),
        }),
        mensual({
          id: 'desglose',
          radio: 12,
          final: true,
          disponible: hayExtras,
          texto: t(
            'If a category groups several payees, like two subscriptions, I show them one by one. The ▲ marks the one that has gone up.',
          ),
          objetivo: () =>
            uno(`${MENSUALES} [data-testid="pago-fijo-desglose"]`),
        }),
      ],
    },
    {
      id: 'estados-anuales',
      texto: t('What do "On track", "Missing" and "Covered" mean?'),
      pasos: [
        anual({
          id: 'al-dia',
          radio: 12,
          disponible: () => hayEstado('anuales', 'al-dia', 'faltan'),
          texto: t(
            '"On track": you have already assigned to its category what is due this month. "Missing X this month": what you still need to assign to it this month.',
          ),
          objetivo: () => filaAnual('al-dia', 'faltan'),
        }),
        anual({
          id: 'atrasada',
          radio: 12,
          disponible: () => hayEstado('anuales', 'atrasada', 'cubierta'),
          texto: t(
            '"Behind": the date has passed and what is saved is not enough. "Covered": you have already saved the whole expense.',
          ),
          objetivo: () => filaAnual('atrasada', 'cubierta'),
        }),
        anual({
          id: 'sin-objetivo',
          radio: 12,
          final: true,
          disponible: () => hayEstado('anuales', 'sin-objetivo'),
          texto: t(
            'If it says "set a yearly target", the category is in an annual group but without an amount or date. Open it and give it a target.',
          ),
          objetivo: () => filaAnual('sin-objetivo'),
        }),
      ],
    },
    {
      id: 'elegir',
      texto: t('How do I choose what shows up in "Annual"?'),
      pasos: [
        {
          id: 'brujula',
          escena: 'brujula',
          radio: 20,
          texto: t(
            'Open the compass and tap "Choose annual groups": there you tick whole groups or single categories. Only what you tick shows up.',
          ),
          objetivo: () => uno('[data-testid="opcion-grupos-anuales"]'),
        },
        anual({
          id: 'nueva',
          radio: 18,
          final: true,
          texto: t(
            'If you have never opened it, categories with a yearly target show up on their own. After that, only what you tick: a new category has to be ticked there.',
          ),
          objetivo: () => {
            const cab = uno('[data-testid="anual-cabecera"]');
            return cab.length > 0 ? cab : vacio('anuales');
          },
        }),
      ],
    },
  ];
}
